/**
 * admin-ticket-catalog.ts - 수강권 카탈로그(폼 2-A) Firestore 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/ticketCatalog/{ticketId}
 *
 * 카탈로그 등록(create)은 백엔드 Cloud Function `createTicketCatalog`을 통해서만
 * 수행한다. 본 모듈은 admin-web에서 read만 담당한다.
 */

import { collection, getDocs, orderBy, query, type Timestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";

/**
 * 카탈로그 문서 스키마.
 *
 * 백엔드 `createTicketCatalog` payload와 1:1.
 * `createdAt`은 서버 타임스탬프(설정 직후 read하면 null일 수 있음 — UI에서 방어).
 */
export interface TicketCatalogItem {
  id: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
  createdAt?: Timestamp | null;
}

function ticketCatalogCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "ticketCatalog",
  );
}

/**
 * 센터의 모든 수강권 카탈로그를 createdAt 내림차순(최근 등록 우선)으로 반환한다.
 *
 * 소프트 삭제된(`archivedAt` 있는) 카탈로그는 제외한다. `orderBy("createdAt")`에
 * `where("archivedAt")`를 더하면 복합 인덱스가 필요하므로 클라이언트에서 필터한다.
 */
export async function getAdminTicketCatalogList(
  ownerUid: string,
  centerId: number,
): Promise<TicketCatalogItem[]> {
  const col = ticketCatalogCollection(ownerUid, centerId);
  const q = query(col, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs
    .filter((d) => (d.data() as { archivedAt?: unknown }).archivedAt == null)
    .map((d) => {
      const data = d.data() as Omit<TicketCatalogItem, "id">;
      return { id: d.id, ...data };
    });
}
